import axios from 'axios';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace(/\/$/, '');

// A fully separate axios instance/session from the tenant app's `api` —
// different localStorage key, different 401 redirect target — so an admin
// session and a tenant session can never collide in the same browser.
export const adminApi = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_auth_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    const isAdminLoginRequest = error.config?.url?.includes('/auth/admin-login');
    if (error.response?.status === 401 && !isAdminLoginRequest) {
      localStorage.removeItem('admin_auth_token');
      window.location.href = '/admin/login';
    }
    if (!error.response) {
      error.message = `Unable to reach the backend API at ${API_BASE_URL}. Make sure the backend server is running.`;
    }
    return Promise.reject(error);
  }
);

export const adminAuthService = {
  login: (email: string, password: string) => adminApi.post('/auth/admin-login', { email, password }),
  logout: () => adminApi.post('/auth/logout'),
  getMe: () => adminApi.get('/auth/me'),
};

export const adminDashboardService = {
  getStats: () => adminApi.get('/admin/dashboard/stats'),
};

export const adminAnalyticsService = {
  getFarmAnalytics: () => adminApi.get('/admin/analytics/farms'),
  getSuperAdminActivity: (days?: number) => adminApi.get('/admin/analytics/user-activity', { params: { days } }),
};

export const adminBroadcastService = {
  getRecipients: () => adminApi.get('/admin/broadcast/recipients'),
  send: (data: { recipient_ids: string[]; subject: string; message: string }) => adminApi.post('/admin/broadcast/send', data),
};

export const adminSettingsService = {
  getDormantSequence: () => adminApi.get('/admin/settings/dormant-sequence'),
  updateDormantSequence: (enabled: boolean) => adminApi.put('/admin/settings/dormant-sequence', { enabled }),
};

export const adminFarmService = {
  getAll: (params?: any) => adminApi.get('/admin/farms', { params }),
  create: (data: any) => adminApi.post('/admin/farms', data),
  update: (id: string, data: any) => adminApi.put(`/admin/farms/${id}`, data),
  delete: (id: string) => adminApi.delete(`/admin/farms/${id}`),
};

export const adminUserService = {
  getAll: (params?: any) => adminApi.get('/admin/users', { params }),
  updateStatus: (id: string, status: string) => adminApi.patch(`/admin/users/${id}/status`, { status }),
};

export const adminSubscriptionService = {
  getAll: () => adminApi.get('/subscription/all'),
  update: (ownerId: string, data: any) => adminApi.put(`/admin/subscriptions/${ownerId}`, data),
  getPlans: () => adminApi.get('/subscription/plans'),
};

export const adminRecordsService = {
  getEmployees: (farmId: string) => adminApi.get(`/admin/farms/${farmId}/employees`),

  getEggCollections: (farmId: string) => adminApi.get(`/admin/farms/${farmId}/egg-collections`),
  deleteEggCollection: (id: string) => adminApi.delete(`/admin/egg-collections/${id}`),

  getSales: (farmId: string) => adminApi.get(`/admin/farms/${farmId}/sales`),
  deleteSale: (id: string) => adminApi.delete(`/admin/sales/${id}`),

  getFlocks: (farmId: string) => adminApi.get(`/admin/farms/${farmId}/flocks`),
  deleteFlock: (id: string) => adminApi.delete(`/admin/flocks/${id}`),

  getActivities: (farmId: string) => adminApi.get(`/admin/farms/${farmId}/activities`),
  deleteActivity: (id: string) => adminApi.delete(`/admin/activities/${id}`),

  getCustomers: (farmId: string) => adminApi.get(`/admin/farms/${farmId}/customers`),
  deleteCustomer: (id: string) => adminApi.delete(`/admin/customers/${id}`),

  getInventory: (farmId: string) => adminApi.get(`/admin/farms/${farmId}/inventory`),
  deleteInventoryItem: (id: string) => adminApi.delete(`/admin/inventory/${id}`),

  getExpenses: (farmId: string) => adminApi.get(`/admin/farms/${farmId}/expenses`),
  deleteExpense: (id: string) => adminApi.delete(`/admin/expenses/${id}`),

  getCreditors: (farmId: string) => adminApi.get(`/admin/farms/${farmId}/creditors`),
  deleteCreditor: (id: string) => adminApi.delete(`/admin/creditors/${id}`),
};

export default adminApi;
